<template>
  <div class="audit-dashboard pa-4">
    <v-card flat class="border-light p-2">
      <v-data-table
        :headers="headers"
        :items="auditLogs"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="custom-audit-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50],
          'show-first-last-page': true
        }"
      >
        <!-- Module Column -->
        <!-- <template v-slot:[`item.module`]="{ item }">
          <span class="font-weight-medium grey--text text--darken-2">
            {{ item.module }}
          </span>
        </template> -->

        <!-- Identification Column -->
        <template v-slot:[`item.recordId`]="{ item }">
          <a
            class="text-body-2 font-weight-bold primary--text text-decoration-none"
            @click="showDetails(item)"
          >
            {{ getIdentificationNumber(item) }}
          </a>
        </template>

        <!-- Action Column -->
        <template v-slot:[`item.action`]="{ item }">
          <v-chip
            small
            label
            :color="getActionColor(item.action)"
            dark
            class="font-weight-bold text-uppercase"
          >
            {{ item.action }}
          </v-chip>
        </template>

        <!-- Field Column -->
        <template v-slot:[`item.fieldChanged`]="{ item }">
          <span class="text-body-2 grey--text text--darken-2">
            {{ formatFieldName(item.fieldChanged) }}
          </span>
        </template>

        <!-- User Column -->
        <template v-slot:[`item.whoChanged`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="grey lighten-2" class="mr-2">
              <span class="grey--text text--darken-2 text-caption">
                {{ (item.whoChanged?.firstName || '?')[0] }}
              </span>
            </v-avatar>
            <span class="text-body-2"
              >{{ item.whoChanged?.firstName || 'System' }}
              {{ item.whoChanged?.lastName || '' }}</span
            >
          </div>
        </template>

        <!-- Timestamp Column -->
        <template v-slot:[`item.createdAt`]="{ item }">
          <span class="text-body-2">{{ formatDate(item.createdAt) }}</span>
        </template>

        <!-- Remarks Column -->
        <template v-slot:[`item.remarks`]="{ item }">
          <span class="text-caption grey--text">
            {{ item.remarks || '-' }}
          </span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="1000px" scrollable>
      <v-card v-if="selectedAudit" class="p-4">
        <v-card-title class="grey lighten-4 py-3 px-4">
          <v-icon left>mdi-history</v-icon>
          Audit Details Record
          <v-spacer></v-spacer>
          <v-chip outlined small class="mr-2">{{ selectedAudit.module }}</v-chip>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4 bg-white">
          <!-- Summary Banner -->
          <div class="d-flex align-center mb-6 pa-3 grey lighten-5 rounded border">
            <div class="mr-6">
              <div class="text-caption grey--text">Action</div>
              <div
                class="font-weight-bold"
                :class="`${getActionColor(selectedAudit.action)}--text`"
              >
                {{ selectedAudit.action }}
              </div>
            </div>
            <div class="mr-6 border-l pl-6">
              <div class="text-caption grey--text">Entity Ref</div>
              <div class="font-weight-bold">{{ getIdentificationNumber(selectedAudit) }}</div>
            </div>
            <div class="mr-6 border-l pl-6">
              <div class="text-caption grey--text">Performed By</div>
              <div class="font-weight-bold">
                {{ selectedAudit.whoChanged?.firstName }} {{ selectedAudit.whoChanged?.lastName }}
              </div>
            </div>
            <div class="border-l pl-6">
              <div class="text-caption grey--text">Date & Time</div>
              <div class="font-weight-bold">{{ formatDate(selectedAudit.createdAt) }}</div>
            </div>
          </div>

          <!-- Structured Changes -->
          <div v-if="selectedAudit.action === 'UPDATE'">
            <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-compare</v-icon>
              Data Comparison
            </div>

            <v-simple-table dense class="border rounded">
              <template v-slot:default>
                <thead class="grey lighten-4">
                  <tr>
                    <th class="text-left font-weight-bold">Property</th>
                    <th class="text-left font-weight-bold">From Value</th>
                    <th class="text-left font-weight-bold">To Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="change in getParsedChanges(selectedAudit)" :key="change.key">
                    <td class="font-weight-medium text-caption">
                      {{ formatFieldName(change.key) }}
                    </td>
                    <td class="py-2">
                      <div class="diff-box old">{{ formatDisplayValue(change.from) }}</div>
                    </td>
                    <td class="py-2">
                      <div class="diff-box new">{{ formatDisplayValue(change.to) }}</div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div v-else>
            <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-database</v-icon>
              Record Snapshot
            </div>
            <v-row dense class="border rounded ma-0">
              <v-col
                v-for="item in getStructuredData(selectedAudit.to || selectedAudit.from)"
                :key="item.key"
                cols="12"
                sm="4"
                class="pa-3 border-b border-r"
              >
                <div class="text-caption grey--text font-weight-bold">
                  {{ formatFieldName(item.key) }}
                </div>
                <div class="text-body-2">{{ formatDisplayValue(item.value) }}</div>
              </v-col>
            </v-row>
          </div>

          <!-- Audit Attachments Section -->
          <div class="mt-6 pt-6 border-t">
            <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-camera-outline</v-icon>
                Audit Evidence
              </div>
              <v-progress-circular
                v-if="loadingDocs"
                indeterminate
                size="16"
                width="2"
                color="primary"
              ></v-progress-circular>
            </div>

            <div v-if="documents && documents.length > 0">
              <!-- Date Chips Selection -->
              <div class="mb-4">
                <div class="text-caption grey--text mb-2">Select Date:</div>
                <v-chip-group v-model="selectedDateIndex" mandatory color="primary" active-class="primary--text">
                  <v-chip
                    v-for="(group, idx) in documents"
                    :key="idx"
                    outlined
                    small
                  >
                    {{ $dayjs(group.date).format('DD MMM, YYYY') }}
                  </v-chip>
                </v-chip-group>
              </div>

              <!-- Attachment Chips Selection (if multiple on same date) -->
              <div v-if="currentAttachments.length > 1" class="mb-4">
                <div class="text-caption grey--text mb-2">Select Image:</div>
                <v-chip-group v-model="selectedAttachmentIndex" mandatory color="primary">
                  <v-chip
                    v-for="(att, idx) in currentAttachments"
                    :key="idx"
                    outlined
                    x-small
                  >
                    Image {{ idx + 1 }}
                  </v-chip>
                </v-chip-group>
              </div>

              <!-- Main Image Display Container -->
              <div v-if="selectedImage" class="image-audit-viewer border rounded bg-grey-lighten-4 pa-2">
                <v-img
                  :src="s3BaseUrl + selectedImage.fileUrl"
                  contain
                  max-height="600"
                  class="rounded cursor-zoom-in"
                  @click="openDocument(s3BaseUrl + selectedImage.fileUrl)"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="primary lighten-4"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <div class="d-flex align-center justify-space-between mt-3 px-2">
                  <div class="text-caption grey--text d-flex align-center">
                    <v-icon x-small class="mr-1">mdi-file-clock</v-icon>
                    Captured: {{ $dayjs(selectedImage.lastModified).format('hh:mm:ss A') }}
                  </div>
                  <div class="text-caption grey--text">
                    Size: {{ (selectedImage.size / 1024).toFixed(1) }} KB
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="!loadingDocs" class="text-center pa-8 grey lighten-5 rounded border-dashed">
              <v-icon large color="grey lighten-1" class="mb-2">mdi-image-off-outline</v-icon>
              <div class="text-caption grey--text font-italic">No audit evidence screenshots found for this record.</div>
            </div>
          </div>

          <!-- Metadata Toggle -->
          <v-expansion-panels flat class="mt-6 border rounded">
            <v-expansion-panel>
              <v-expansion-panel-header class="grey--text text-caption py-2">
                View Raw JSON Metadata
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-sheet
                  color="grey darken-4"
                  dark
                  class="pa-4 rounded overflow-auto"
                  style="max-height: 300px"
                >
                  <pre class="json-code">{{ JSON.stringify(selectedAudit, null, 2) }}</pre>
                </v-sheet>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'AuditTable',
  props: {
    caseConfig: Object
  },
  data() {
    return {
      loading: false,
      auditLogs: [],
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      headers: [
        // { text: 'Module', value: 'module', sortable: true },
        { text: 'Case/Loan No', value: 'recordId', sortable: false },
        { text: 'Action', value: 'action', sortable: true },
        { text: 'Field', value: 'fieldChanged', sortable: true },
        { text: 'Changed By', value: 'whoChanged', sortable: false },
        { text: 'Timestamp', value: 'createdAt', sortable: true },
        { text: 'Remarks', value: 'remarks', sortable: true }
      ],
      detailsDialog: false,
      selectedAudit: null,
      loadingDocs: false,
      documents: [],
      selectedDateIndex: 0,
      selectedAttachmentIndex: 0,
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL || ''
    }
  },
  computed: {
    currentAttachments() {
      if (this.documents[this.selectedDateIndex]) {
        return this.documents[this.selectedDateIndex].attachments || []
      }
      return []
    },
    selectedImage() {
      return this.currentAttachments[this.selectedAttachmentIndex] || null
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchAudits()
      },
      deep: true
    },
    '$route.params.subCase': {
      handler() {
        this.fetchAudits()
      }
    }
  },
  methods: {
    async fetchAudits() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options
        const subCaseId = this.$route.params.subCase

        const params = {
          page,
          limit: itemsPerPage,
          sortBy: sortBy && sortBy[0],
          sortOrder: sortDesc && sortDesc[0] ? 'desc' : 'asc',
          subCase: subCaseId
        }

        const response = await this.$axios.get(axiosUrl.cases.getAuditLogs, { params })
        const responseData = response.data
        this.auditLogs = responseData?.data || []
        this.totalItems =
          responseData?.total !== undefined ? responseData.total : this.auditLogs.length
      } catch (error) {
        console.error('Audit Error:', error)
      } finally {
        this.loading = false
      }
    },
    getIdentificationNumber(item) {
      if (!item) return '-'
      if (item.caseId && typeof item.caseId === 'object' && item.caseId.caseNo)
        return item.caseId.caseNo
      if (item.loanId && typeof item.loanId === 'object' && item.loanId.loanNo)
        return item.loanId.loanNo

      const toObj = item.to || {}
      return toObj.caseNo || toObj.applicationNumber || toObj.loanNo || item.recordId || '-'
    },
    getActionColor(action) {
      const colors = {
        CREATE: 'success',
        UPDATE: 'info',
        DELETE: 'error'
      }
      return colors[action] || 'grey'
    },
    formatDate(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD-MM-YYYY hh:mm A')
    },
    formatFieldName(field) {
      if (!field) return '-'
      return field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    },
    formatDisplayValue(val) {
      if (val === null || val === undefined) return '-'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (typeof val === 'object') {
        if (Array.isArray(val)) return `${val.length} Items`
        return val.name || val.fullName || val.label || 'Details'
      }
      return val
    },
    getParsedChanges(item) {
      if (!item || item.action !== 'UPDATE') return []
      if (item.fieldChanged && (item.from !== undefined || item.to !== undefined)) {
        return [{ key: item.fieldChanged, from: item.from, to: item.to }]
      }
      if (typeof item.from === 'object' && typeof item.to === 'object') {
        const keys = Array.from(
          new Set([...Object.keys(item.from || {}), ...Object.keys(item.to || {})])
        )
        return keys
          .filter((k) => !k.startsWith('_') && k !== 'updatedAt')
          .map((k) => ({ key: k, from: item.from[k], to: item.to[k] }))
          .filter((c) => JSON.stringify(c.from) !== JSON.stringify(c.to))
      }
      return []
    },
    getStructuredData(obj) {
      if (!obj || typeof obj !== 'object') return []
      const ignore = [
        '_id',
        '__v',
        'updatedAt',
        'createdAt',
        'createdBy',
        'updatedBy',
        'loanDisbursementsData',
        'loanWorkflowData',
        'emiWorkflowData'
      ]
      return Object.keys(obj)
        .filter((k) => !ignore.includes(k) && obj[k] !== null && obj[k] !== undefined)
        .map((k) => ({ key: k, value: obj[k] }))
    },
    async fetchDocuments(item) {
      const docId = item.caseId?._id || item.caseId
      if (!docId) return

      this.loadingDocs = true
      this.documents = []
      this.selectedDateIndex = 0
      this.selectedAttachmentIndex = 0

      try {
        const params = {
          docId: docId,
          useCase: this.caseConfig?.useCase || 'loan_management'
        }
        const response = await this.$axios.get(axiosUrl.cases.uploadCaseDocuments, { params })
        this.documents = response.data?.data || []
      } catch (error) {
        console.error('Error fetching documents:', error)
      } finally {
        this.loadingDocs = false
      }
    },
    showDetails(item) {
      this.selectedAudit = item
      this.detailsDialog = true
      this.fetchDocuments(item)
    },
    openDocument(url) {
      if (!url) return
      window.open(url, '_blank')
    }
  },
  mounted() {
    this.fetchAudits()
  }
}
</script>

<style scoped>
.custom-audit-table >>> th {
  background-color: #f5f5f5 !important;
  color: #333 !important;
  font-weight: bold !important;
}

.border-light {
  border: 1px solid #e0e0e0 !important;
}

.border {
  border: 1px solid #e0e0e0 !important;
}
.border-b {
  border-bottom: 1px solid #e0e0e0 !important;
}
.border-r {
  border-right: 1px solid #e0e0e0 !important;
}
.border-l {
  border-left: 1px solid #e0e0e0 !important;
}

.diff-box {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.4;
  word-break: break-all;
}
.diff-box.old {
  background-color: #fff5f5;
  color: #c53030;
  text-decoration: line-through;
}
.diff-box.new {
  background-color: #f0fff4;
  color: #276749;
  font-weight: bold;
}

.json-code {
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.border-dashed {
  border: 2px dashed #e0e0e0 !important;
}

.image-audit-viewer {
  background-color: #fcfcfc;
  transition: all 0.3s ease;
}

.cursor-zoom-in {
  cursor: zoom-in;
}
</style>
